import { Image } from "antd";
import dayjs from "dayjs";
import LinkIcon from "../../assets/images/external-link.png";
import FolderIcon from "../../assets/images/folder.png";
import PDF from "../../assets/images/pdf.svg";
import { dateFormat } from "../../constants/constants";
import { useGetAnnouncementDetailsQuery } from "../../features/announcement/announcementApi";
import useError from "../../hooks/useError";
import useModal, { ModalType } from "../../hooks/useModal";
import {
  AnnouncementDetailsResponseType,
  ModalComponentProps,
} from "../../types";
import { getExtension } from "../../utils/string";
import InfoItem from "../common/InfoItem";
import CustomModal from "../ui/CustomModal";
import Loader from "../ui/Loader";
import { targetTypes } from "./ManageAnnouncement";
import ViewTargetData from "./ViewTargetData";

interface ViewManageAnnouncementProps extends ModalComponentProps {
  handleEditOpen: (value: Omit<ModalType, "open">) => void;
}

export type ViewDetailsType =
  AnnouncementDetailsResponseType["data"]["announcement"]["target"];

export default function ViewManageAnnouncement({
  handleClose,
  handleEditOpen,
  data,
  ...rest
}: ViewManageAnnouncementProps) {
  const {
    open,
    handleOpen,
    handleClose: handleCloseDetails,
  } = useModal<ViewDetailsType>();
  const {
    isLoading: isAnnouncementLoading,
    isError: isAnnouncementDetailsError,
    error: announcementDetailsError,
    data: announcementDetails,
  } = useGetAnnouncementDetailsQuery(data || "", {
    skip: !data,
  });
  useError(
    isAnnouncementDetailsError,
    announcementDetailsError,
    "Get announcement data fail"
  );

  const detailsData = announcementDetails?.data?.announcement;

  const isPDF = getExtension(detailsData?.attachment!)?.toLowerCase() === "pdf";
  const isFile = ".mp4, .mov, .avi, .mkv, .mp3, .wav, .aac"?.includes(
    getExtension(detailsData?.attachment!)?.toLowerCase()!
  );

  return (
    <CustomModal
      title="Announcement Details"
      {...rest}
      handleClose={handleClose}
      width={600}
      footer={null}
    >
      {open?.type === "VIEW" ? (
        <ViewTargetData
          open={open?.open}
          data={open?.data}
          handleClose={handleCloseDetails}
        />
      ) : null}
      {isAnnouncementLoading ? (
        <Loader />
      ) : (
        <>
          <InfoItem title="Type">{detailsData?.type}</InfoItem>
          <InfoItem title="Title">{detailsData?.title}</InfoItem>
          <InfoItem title="Short Description">
            {detailsData?.short_description}
          </InfoItem>

          <InfoItem title="Duration">
            <div
              dangerouslySetInnerHTML={{
                __html: `${dayjs(
                  `${detailsData?.scheduled_info?.start_date}T${detailsData?.scheduled_info?.start_time}`
                ).format(`${dateFormat} hh:mm A`)} - <br /> ${dayjs(
                  `${detailsData?.scheduled_info?.end_date}T${detailsData?.scheduled_info?.end_time}`
                ).format(`${dateFormat} hh:mm A`)}`,
              }}
            />
          </InfoItem>
          <InfoItem title="Active Between" className="!text-accent select-none">
            {detailsData?.target?.type === "all" ? (
              "All"
            ) : (
              <div
                className="cursor-pointer select-none"
                onClick={() => {
                  handleOpen({
                    type: "VIEW",
                    data: detailsData?.target,
                  });
                }}
              >
                {detailsData?.target?.count}{" "}
                {
                  targetTypes?.find(
                    (item) => item?.value === detailsData?.target?.type
                  )?.label
                }
              </div>
            )}
          </InfoItem>
          <InfoItem title="Created By">{detailsData?.created_by}</InfoItem>
          <InfoItem title="Created On">{detailsData?.created_at}</InfoItem>
          <InfoItem title="Updated By">
            {detailsData?.updated_by || undefined}
          </InfoItem>
          <InfoItem title="Updated On">
            {detailsData?.updated_by ? detailsData?.updated_at : undefined}
          </InfoItem>
          <InfoItem title="Description" rootClassName="border-b-0">
            {detailsData?.description ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: detailsData?.description,
                }}
              />
            ) : undefined}
          </InfoItem>
          <InfoItem title="Attachment">
            {detailsData?.attachment ? (
              <div className="flex items-end gap-2">
                <Image
                  src={
                    isPDF ? PDF : isFile ? FolderIcon : detailsData?.attachment
                  }
                  width={50}
                  height={40}
                  className={
                    isPDF || isFile ? "object-contain" : "object-cover"
                  }
                  preview={isPDF || isFile ? false : true}
                />
                <span className="text-accent flex items-center gap-1 text-xs mt-1">
                  <a
                    href={detailsData?.attachment}
                    target="_blank"
                    className="-mt-[2px] cursor-pointer"
                    rel="noreferrer"
                    download="Attachment"
                  >
                    <img className="size-3" src={LinkIcon} alt="" />
                  </a>
                </span>
              </div>
            ) : null}
          </InfoItem>

          {/* <Space className="w-full mt-6" size={12}>
            <Button
              className="normal-dark-button modal-button"
              onClick={handleClose}
              size="middle"
            >
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              onClick={() =>
                handleEditOpen({
                  type: "UPDATE",
                  data,
                })
              }
            >
              Edit
            </Button>
          </Space> */}
        </>
      )}
    </CustomModal>
  );
}
